/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { servicesMessages } from 'src/appShell/homePage/homePageServicesMessages';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { globalMessages } from 'src/core/utils/globalMessages';

type ServiceInfo = {
    /**
     * The title of the service.
     */
    title: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The description of the service.
     */
    description: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The icon src name.
     */
    iconSrc: string;

    /**
     * The learn more href link
     */
    learnMoreLink: string;

    /**
     * The learn more link description
     */
    learnMoreAriaLabel: ReactIntl.FormattedMessage.MessageDescriptor;
};

const { styleComponent, styleDiv } = getStylist('HomePageServices');

const ServicesRootContainer = styleDiv<{ isLargeScreen: boolean }>('HomePageServicesRootContainer', {}, ({ customProps }, theme) => ({
    padding: customProps.isLargeScreen ? theme.containerPaddingLarge : theme.containerPaddingSmall
}));

const ServicesTitle = styleComponent<ITextProps>(Text)('HomePageServicesTitle', theme => ({
    fontSize: theme.titleFontSize,
    textAlign: 'center'
}));

const CenteredText = styleComponent<LuisTextProps>(LuisText)('CenteredText', { textAlign: 'center' });

const FullWidthStack = styleComponent<IStackProps>(Stack)('FullWidthStack', { width: '100%' });

const ThirdWidthStack = styleComponent<IStackProps>(Stack)<{ isLargeScreen: boolean }>('ThirdWidthStack', {}, ({ customProps }) => ({
    width: customProps.isLargeScreen ? '33.33%' : '100%'
}));

const StyledLink = styleComponent<ILinkProps>(Link)('StyledLink', () => ({ textDecoration: 'underline' }));

/**
 * Renders the related services section in the home page.
 */
export const HomePageServices = () => {
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    /**
     * Renders a service card that shows the service's
     * title, description, icon, and learn more link.
     *
     * @param service The service to render.
     */
    const renderService = (service: ServiceInfo): JSX.Element => (
        <ThirdWidthStack key={getId()} horizontalAlign="center" tokens={{ childrenGap: 15 }}>
            <img alt={localize(service.title)} src={getAssetsUrl(`assets/images/homePage/${service.iconSrc}`)} />

            <LuisText variant="xLarge" fontWeight={500}>
                {localize(service.title)}
            </LuisText>

            <CenteredText>{localize(service.description)}</CenteredText>

            <StyledLink
                role="link"
                target="_blank"
                rel="noopener noreferrer"
                href={service.learnMoreLink}
                aria-label={localize(service.learnMoreAriaLabel)}>
                <LuisText styles={{ root: { textTransform: 'uppercase' } }} fontWeight={500}>
                    {localize(globalMessages.learnMore)}
                </LuisText>
            </StyledLink>
        </ThirdWidthStack>
    );

    return (
        <ServicesRootContainer role="region" customProps={{ isLargeScreen: isXLargeOrGreater }}>
            <Stack horizontalAlign="center" tokens={{ childrenGap: 50 }}>
                <ServicesTitle variant="xxLarge" as="h3" block>
                    {localize(servicesMessages.mainTitle)}
                </ServicesTitle>

                <CenteredText variant="xxLarge" block>
                    {localize(servicesMessages.subTitle)}
                </CenteredText>

                <FullWidthStack tokens={{ childrenGap: 30 }}>
                    <Stack
                        horizontal={isXLargeOrGreater}
                        horizontalAlign="space-between"
                        verticalAlign="center"
                        tokens={{ childrenGap: 30 }}>
                        {serviceInfo.slice(0, 3).map(renderService)}
                    </Stack>

                    <Stack
                        horizontal={isXLargeOrGreater}
                        horizontalAlign="space-between"
                        verticalAlign="center"
                        tokens={{ childrenGap: 30 }}>
                        {serviceInfo.slice(3, 6).map(renderService)}
                    </Stack>
                </FullWidthStack>
            </Stack>
        </ServicesRootContainer>
    );
};

const serviceInfo: ServiceInfo[] = [
    {
        title: servicesMessages.azureBotServiceTitle,
        description: servicesMessages.azureBotServiceDescription,
        iconSrc: 'azureBotServiceIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/bot-service/',
        learnMoreAriaLabel: servicesMessages.learnMoreAzureService
    },
    {
        title: servicesMessages.qnaTitle,
        description: servicesMessages.qnaDescription,
        iconSrc: 'qnaMakerIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/cognitive-services/qna-maker/',
        learnMoreAriaLabel: servicesMessages.learnMoreQnAMaker
    },
    {
        title: servicesMessages.textAnalyticsTitle,
        description: servicesMessages.textAnalyticsDescription,
        iconSrc: 'textAnalyticsIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/cognitive-services/text-analytics/',
        learnMoreAriaLabel: servicesMessages.learnMoreTextAnalytics
    },
    {
        title: servicesMessages.bingSpellTitle,
        description: servicesMessages.bingSpeechDescription,
        iconSrc: 'bingSpellCheckIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/cognitive-services/spell-check/',
        learnMoreAriaLabel: servicesMessages.learnMoreBingSpellCheck
    },
    {
        title: servicesMessages.bingSpeechTitle,
        description: servicesMessages.bingSpeechDescription,
        iconSrc: 'bingSpeechIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/cognitive-services/speech/',
        learnMoreAriaLabel: servicesMessages.learnMoreBingSpeech
    },
    {
        title: servicesMessages.azureSearchTitle,
        description: servicesMessages.azureSearchDescription,
        iconSrc: 'azureSearchIcon.png',
        learnMoreLink: 'https://azure.microsoft.com/services/search/',
        learnMoreAriaLabel: servicesMessages.learnMoreAzureSearch
    }
];
